// src/components/ChatWindow.tsx
import React, { useEffect, useRef, useState } from "react";
import ChatMessage from "./ChatMessage";
import SourcesContainer from "./SourcesContainer";
import AnswerDisplay from "./AnswerDisplay";
import { v4 as uuidv4 } from "uuid";
import { Chat, BiasColor } from "../hooks/types";


/**
 * ChatWindow Component
 *
 * This component displays a scrollable chat window with chat messages, answer displays,
 * and a container for sources related to each chat message.
 *
 * Props:
 * - `chats`: An array of `Chat` objects representing individual chat entries. Each chat includes:
 *   - `query`: The user's query string.
 *   - `sources`: An array of sources linked to the chat response.
 *   - `answer`: The response to the user's query, if available.
 *   - `error`: Any error message related to the chat response.
 *   - `loading`: A boolean indicating whether the response is currently loading.
 *   - `status`: A status message associated with the chat processing state.
 *
 * Internal State:
 * - `biasVisibility`: A map defining visibility of bias color categories, used for filtering chat display elements by bias types.
 * - `biasColorToBiasNameMap`: A map to store bias names corresponding to specific colors, updating dynamically as components render.
 * - `renderStage`: Tracks stages in the rendering process, allowing components to use updated `biasColorToBiasNameMap` values at the appropriate time.
 *
 * Rendering Logic:
 * - Each `Chat` renders a `ChatMessage`, a `SourcesContainer`, and optionally an `AnswerDisplay` if an answer is available.
 * - Uses a scroll ref (`bottomRef`) to auto-scroll to the latest chat message.
 *
 * Usage:
 * Place `ChatWindow` within a parent container to display a live chat interface, passing an array of `Chat` objects as `chats`.
 *
 * Example:
 * ```jsx
 * <ChatWindow chats={chatData} />
 * ```
 *
 * @module ChatWindow
 */

// Array of chat objects to display in the chat window
type ChatWindowProps = {
  chats: Chat[];
};

/**
 * ChatWindow Component
 * Renders a scrollable chat window with messages, sources, and answers.
 * Auto-scrolls to the latest message whenever new data is added.
 *
 * @param ChatWindowProps props - The chats for the component
 * @returns JSX.Element - The JSX to render the chat window
 */
const ChatWindow: React.FC<ChatWindowProps> = ({ chats }) => {
  // Ref to the bottom of the chat window for auto-scrolling
  const bottomRef = useRef<HTMLDivElement>(null);

  // State to track which bias color is currently visible in the chat window
  const [visibleBiasColor, setVisibleBiasColor] = useState<BiasColor | "">("green");

  /**
   * Effect hook that scrolls the chat window to the bottom when `chats` changes.
   */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chats]);

  return (
    <div
    className="w-[80%] max-w-[1300px] h-full px-4 pt-4 bg-screen-black text-white flex flex-col overflow-y-auto gap-20"
      data-testid="chat-window"
    >
      {chats.map((chat) => (
      <div 
        className="flex flex-col items-center gap-6 min-h-fit transition-all duration-300" 
        key={uuidv4()}
      >
          <div ref={bottomRef} data-testid="bottom-ref" />
          <ChatMessage query={chat.query} data-testid="chat-message" />
          <SourcesContainer
            sources={chat.sources}
            error={chat.error}
            loading={chat.loading}
            status={chat.status}
            data-testid="sources-container"
            visibleBiasColor={visibleBiasColor}
            setVisibleBiasColor={setVisibleBiasColor}
          />
        {chat.answer && (
          <div className="w-full flex justify-center mt-4">
            <AnswerDisplay 
              query={chat.query} 
              answer={chat.answer} 
              visibleBiasColor={visibleBiasColor} 
            />
          </div>
        )}
        </div>
      ))}
    </div>
  );
};

export default ChatWindow;
